import React, { useState, useMemo } from 'react';
import { Package, MapPin, Search, BarChart3 } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import CustomPieChart from 'components/common/CustomPieChart';
import KPICard from 'components/common/KPICard';

const EquipamentosParaTestesTable = ({ data, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Lista de cidades (excluindo id_produto, Produto e Total)
  const cidades = useMemo(() => {
    if (data.length === 0) return [];
    const firstItem = data[0];
    return Object.keys(firstItem).filter(key => 
      key !== 'id_produto' && key !== 'Produto' && key !== 'Total'
    ).sort();
  }, [data]);

  // Filtrar dados
  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchesSearch = (item.Produto && String(item.Produto).toLowerCase().includes(searchTerm.toLowerCase())) ||
                           item.id_produto.toString().includes(searchTerm);
      return matchesSearch;
    });
  }, [data, searchTerm]);

  // Calcular KPIs
  const kpis = useMemo(() => {
    const totalProdutos = filteredData.length;
    const totalEquipamentos = filteredData.reduce((total, item) => total + parseFloat(item.Total || 0), 0);
    const cidadesComEstoque = new Set(
      filteredData.flatMap(item => 
        cidades.filter(cidade => parseFloat(item[cidade] || 0) > 0)
      )
    ).size;
    const produtosComEstoque = filteredData.filter(item => parseFloat(item.Total || 0) > 0).length;
    
    return {
      totalProdutos,
      totalEquipamentos,
      cidadesComEstoque,
      produtosComEstoque
    };
  }, [filteredData, cidades]);

  // Dados para gráfico de pizza - Distribuição por cidade
  const distribuicaoCidade = useMemo(() => {
    const distribuicao = {};
    cidades.forEach(cidade => {
      distribuicao[cidade] = filteredData.reduce((total, item) => 
        total + parseFloat(item[cidade] || 0), 0
      );
    });
    
    return Object.entries(distribuicao)
      .filter(([_, value]) => value > 0)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData, cidades]);

  // Configurar colunas da tabela
  const columns = [
    {
      key: 'id_produto',
      label: 'ID',
      sortable: true,
      className: 'w-[8%]',
      render: (value) => (
        <span className="font-mono text-xs text-[color:var(--text-muted)]">#{value}</span>
      )
    },
    {
      key: 'Produto',
      label: 'Equipamento',
      sortable: true,
      className: 'w-[18%]',
      render: (value) => (
        <div className="font-medium text-[color:var(--text-primary)] text-xs break-words" title={value}>
          {value}
        </div>
      )
    },
    ...cidades.map(cidade => ({
      key: cidade,
      label: cidade,
      sortable: true,
      className: 'w-[10%] text-xs',
      render: (value) => {
        const quantidade = parseFloat(value || 0);
        const isZero = quantidade === 0;
        
        return (
          <div className={`text-right ${isZero ? 'text-[color:var(--text-muted)]' : 'text-[color:var(--status-good)]'}`} title={`${cidade}: ${quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}>
            <span className="font-mono text-xs">
              {quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        );
      }
    })),
    {
      key: 'Total',
      label: 'Total',
      sortable: true,
      className: 'w-[8%]',
      render: (value) => {
        const total = parseFloat(value || 0);
        return (
          <div className="text-right font-bold text-[color:var(--accent)]" title={`Total: ${total.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}>
            <span className="font-mono text-xs">
              {total.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        );
      }
    }
  ];

  const totalDistribuicao = useMemo(
    () => distribuicaoCidade.reduce((total, city) => total + city.value, 0),
    [distribuicaoCidade]
  );

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {loading ? (
          [...Array(4)].map((_, index) => (
            <div key={index} className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
              <div className="animate-pulse">
                <div className="h-3 w-1/2 rounded bg-[color:var(--surface-muted)] mb-3" />
                <div className="h-7 w-1/3 rounded bg-[color:var(--surface-muted)] mb-3" />
                <div className="h-10 w-10 rounded-lg bg-[color:var(--surface-muted)] ml-auto" />
              </div>
            </div>
          ))
        ) : (
          <>
            <KPICard
              title="Total de Produtos"
              value={kpis.totalProdutos.toLocaleString('pt-BR')}
              icon={Package}
              color="blue"
            />
            <KPICard
              title="Total Equipamentos"
              value={kpis.totalEquipamentos.toLocaleString('pt-BR')}
              icon={Package}
              color="green"
            />
            <KPICard
              title="Cidades com Estoque"
              value={kpis.cidadesComEstoque}
              icon={MapPin}
              color="purple"
            />
            <KPICard
              title="Produtos com Estoque"
              value={kpis.produtosComEstoque.toLocaleString('pt-BR')}
              icon={Package}
              color="yellow"
            />
          </>
        )}
      </div>


      {/* Gráfico de Distribuição por Cidade */}
      {loading ? (
        <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-56 rounded bg-[color:var(--surface-muted)]" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-72 rounded-xl bg-[color:var(--surface-muted)]" />
              <div className="space-y-3">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className="h-12 rounded-lg bg-[color:var(--surface-muted)]" />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : distribuicaoCidade.length > 0 && (
        <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-[color:var(--accent)]" />
            Distribuição por Cidade
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico na esquerda */}
            <div className="flex justify-center">
              <div className="w-full">
                <CustomPieChart
                  data={distribuicaoCidade}
                  title=""
                  dataKey="value"
                  nameKey="name"
                  height={400}
                  showLegend={false}
                  useEnhancedLegend={false}
                  maxLegendItems={8}
                  sortDescending={true}
                  className="border-0 shadow-none"
                />
              </div>
            </div>
            
            {/* Legenda do gráfico na direita */}
            <div className="flex flex-col justify-center">
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {distribuicaoCidade.map((item, index) => {
                  const colors = ['var(--accent)', 'var(--status-good)', 'var(--status-warn)', 'var(--status-bad)', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];
                  const color = colors[index % colors.length];
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)]">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div 
                          className="w-4 h-4 rounded-full ring-2 ring-[color:var(--surface)]" 
                          style={{ backgroundColor: color }}
                        ></div>
                        <span className="font-medium text-[color:var(--text-primary)] truncate">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-[color:var(--text-primary)]">
                          {item.value.toLocaleString('pt-BR')}
                        </div>
                        <div className="text-sm text-[color:var(--text-muted)]">
                          {totalDistribuicao > 0 ? ((item.value / totalDistribuicao) * 100).toFixed(1) : '0.0'}%
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
        <div className="px-6 py-4 border-b border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-[color:var(--text-primary)]">
                Equipamentos para Teste
                <span className="ml-2 text-sm text-[color:var(--text-muted)]">
                  ({filteredData.length} de {data.length} equipamentos)
                </span>
              </h3>
            </div>
            
            {/* Componente de Pesquisa Compacto */}
            <div className="flex items-center">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-[color:var(--text-muted)]" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar equipamento ou ID..."
                  disabled={loading}
                  className="block w-64 pl-9 pr-3 py-2 text-sm border border-[color:var(--border)] rounded-md leading-5 bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                />
              </div>
            </div>
          </div>
        </div>
        
        <DataTable
          data={filteredData}
          columns={columns}
          loading={loading}
          searchable={false}
          pagination={true}
          pageSize={15}
        />
      </div>
    </div>
  );
};

export default EquipamentosParaTestesTable;
